import React from 'react';
import { IcMedal, IcTurtle } from 'components/common/icons/EmojiIcons';
import styles from './RunnerAvatar.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export type RunnerState =
    | 'arrived'       // 도착 완료 — 여유 (커피/앉음)
    | 'easy'          // 여유롭게 걷기 (ETA < 약속-5분)
    | 'normal'        // 보통 달리기
    | 'rush'          // 전력질주 (지각 임박)
    | 'panic'         // 얼굴 빨개짐 (10분+ 지각)
    | 'stopped';      // 정지 중 (속도 0)

export interface RunnerAvatarProps {
    state: RunnerState;
    rank?: number;          // 1 = 금메달, 2 = 은메달, 3 = 동메달, 0/undefined = 없음
    isLast?: boolean;       // 꼴등 (거북이 아이콘 표시)
    size?: number;          // px, default 56
    nickname?: string;
    profileImgUrl?: string; // 있으면 머리 자리에 원형 썸네일
}

const RunnerAvatar: React.FC<RunnerAvatarProps> = ({
    state,
    rank,
    isLast,
    size = 56,
    nickname,
    profileImgUrl,
}) => {
    const skin = '#fce0c2';
    const shirt = stateColors(state).shirt;
    const pants = stateColors(state).pants;
    const face = stateColors(state).face; // 빨간 얼굴 표현용

    return (
        <div
            className={cls(`runner-wrap runner-${state}`)}
            style={{ width: size, height: size }}
            aria-label={`${nickname || ''} ${state}`}
        >
            {/* 1등 왕관 */}
            {rank === 1 && (
                <svg className={cls("runner-crown")} viewBox="0 0 24 12" width={size * 0.55}>
                    <path d="M2 10 L2 4 L7 7 L12 2 L17 7 L22 4 L22 10 Z" fill="#FFD54A" stroke="#D4A400" strokeWidth="0.8" />
                    <circle cx="7" cy="7" r="0.9" fill="#fff" />
                    <circle cx="12" cy="2" r="0.9" fill="#fff" />
                    <circle cx="17" cy="7" r="0.9" fill="#fff" />
                </svg>
            )}

            {/* 메달 (2/3등) */}
            {(rank === 2 || rank === 3) && (
                <div className={cls(`runner-medal medal-${rank}`)}><IcMedal size={Math.round(size * 0.32)} /></div>
            )}

            {/* 꼴등 거북이 */}
            {isLast && <div className={cls("runner-turtle")} aria-hidden="true"><IcTurtle size={Math.round(size * 0.32)} /></div>}

            {/* 러너 본체 SVG */}
            <svg
                viewBox="0 0 64 64"
                width={size}
                height={size}
                className={cls(`runner-svg runner-anim-${state}`)}
            >
                {/* 땀방울 (rush, panic) */}
                {(state === 'rush' || state === 'panic') && (
                    <g className={cls("runner-sweat")}>
                        <ellipse cx="48" cy="18" rx="1.8" ry="2.6" fill="#6FD8FF" />
                        <ellipse cx="52" cy="22" rx="1.2" ry="2" fill="#6FD8FF" />
                        {state === 'panic' && (
                            <>
                                <ellipse cx="14" cy="18" rx="1.5" ry="2.4" fill="#6FD8FF" />
                                <ellipse cx="10" cy="24" rx="1.8" ry="2.8" fill="#6FD8FF" />
                            </>
                        )}
                    </g>
                )}

                {/* 얼굴 (빨개짐은 face 색상으로) */}
                <g className={cls("runner-body")}>
                    {profileImgUrl ? (
                        <>
                            {/* 프로필 사진을 머리 위치에 원형 클리핑 */}
                            <defs>
                                <clipPath id={`head-clip-${profileImgUrl.replace(/[^a-z0-9]/gi, '').slice(-8)}`}>
                                    <circle cx="32" cy="18" r="9" />
                                </clipPath>
                            </defs>
                            <image
                                href={profileImgUrl}
                                x="23" y="9" width="18" height="18"
                                clipPath={`url(#head-clip-${profileImgUrl.replace(/[^a-z0-9]/gi, '').slice(-8)})`}
                                preserveAspectRatio="xMidYMid slice"
                            />
                            {/* state 에 따른 테두리 — panic 은 빨갛게 */}
                            <circle
                                cx="32" cy="18" r="9"
                                fill="none"
                                stroke={state === 'panic' ? '#ff3333' : state === 'arrived' ? '#6FCF97' : '#fff'}
                                strokeWidth="1.5"
                            />
                        </>
                    ) : (
                        <>
                            {/* 머리 */}
                            <circle cx="32" cy="18" r="9" fill={face} />
                            {/* 머리카락 */}
                            <path d="M23 16 Q27 8 32 9 Q37 8 41 16 L41 13 Q38 6 32 7 Q26 6 23 13 Z" fill="#3a2a1f" />

                            {/* 눈 */}
                            {state === 'panic' ? (
                                <>
                                    <path d="M28 17 L30 19 M30 17 L28 19" stroke="#222" strokeWidth="1.3" />
                                    <path d="M34 17 L36 19 M36 17 L34 19" stroke="#222" strokeWidth="1.3" />
                                </>
                            ) : state === 'stopped' ? (
                                <>
                                    <path d="M27 18 Q29 17 31 18" stroke="#222" strokeWidth="1.2" fill="none" />
                                    <path d="M33 18 Q35 17 37 18" stroke="#222" strokeWidth="1.2" fill="none" />
                                </>
                            ) : (
                                <>
                                    <circle cx="29" cy="18" r="1.2" fill="#222" />
                                    <circle cx="35" cy="18" r="1.2" fill="#222" />
                                </>
                            )}
                        </>
                    )}

                    {/* 입 — 프로필 사진 있으면 숨김 (사진에 포함됨) */}
                    {!profileImgUrl && state === 'arrived' && (
                        <path d="M29 22 Q32 25 35 22" stroke="#222" strokeWidth="1.2" fill="none" />
                    )}
                    {!profileImgUrl && state === 'easy' && (
                        <path d="M30 22 Q32 24 34 22" stroke="#222" strokeWidth="1.2" fill="none" />
                    )}
                    {!profileImgUrl && state === 'normal' && (
                        <ellipse cx="32" cy="22.5" rx="1.8" ry="0.8" fill="#222" />
                    )}
                    {!profileImgUrl && state === 'rush' && (
                        <ellipse cx="32" cy="23" rx="2.2" ry="1.5" fill="#1a1a1a" />
                    )}
                    {!profileImgUrl && state === 'panic' && (
                        <ellipse cx="32" cy="23" rx="3" ry="2.4" fill="#1a1a1a" />
                    )}
                    {!profileImgUrl && state === 'stopped' && (
                        <line x1="30" y1="22.5" x2="34" y2="22.5" stroke="#222" strokeWidth="1.2" />
                    )}

                    {/* 목·몸통 */}
                    {state === 'arrived' ? (
                        // 앉아있는 자세 — 다리 접힘
                        <>
                            <rect x="26" y="26" width="12" height="14" rx="4" fill={shirt} />
                            <rect x="26" y="39" width="5" height="8" rx="2" fill={pants} />
                            <rect x="33" y="39" width="5" height="8" rx="2" fill={pants} />
                            {/* 커피잔 */}
                            <rect x="40" y="28" width="7" height="8" rx="1" fill="#8B5A3C" />
                            <path d="M47 30 Q50 30 50 33 Q50 35 47 35" stroke="#8B5A3C" strokeWidth="1.5" fill="none" />
                            {/* 김 */}
                            <path d="M42 24 Q41 22 42 20 M45 24 Q44 22 45 20" stroke="#ccc" strokeWidth="1" fill="none" opacity="0.7">
                                <animate attributeName="opacity" values="0.3;0.8;0.3" dur="2s" repeatCount="indefinite" />
                            </path>
                        </>
                    ) : (
                        <>
                            {/* 몸통 (달리는 자세) */}
                            <path
                                d={
                                    state === 'easy' || state === 'stopped'
                                        ? 'M26 26 L38 26 L40 42 L24 42 Z'  // 똑바로
                                        : 'M26 27 L38 25 L42 40 L26 44 Z'  // 앞으로 기울임
                                }
                                fill={shirt}
                            />
                            {/* 팔 */}
                            <g className={cls("runner-arm runner-arm-front")}>
                                <path
                                    d={
                                        state === 'easy'
                                            ? 'M38 28 L44 36 L42 38 L36 30 Z'
                                            : state === 'stopped'
                                                ? 'M38 28 L42 34 L40 35 L36 29 Z'
                                                : state === 'normal'
                                                    ? 'M38 27 L46 32 L44 34 L36 29 Z'
                                                    : 'M38 26 L50 28 L48 31 L36 29 Z' // rush/panic 앞으로 쭉
                                    }
                                    fill={shirt}
                                />
                                <circle
                                    cx={state === 'easy' ? 43 : state === 'stopped' ? 41 : state === 'normal' ? 46 : 50}
                                    cy={state === 'easy' ? 36 : state === 'stopped' ? 34 : state === 'normal' ? 32 : 29}
                                    r="2.2"
                                    fill={skin}
                                />
                            </g>
                            <g className={cls("runner-arm runner-arm-back")}>
                                <path
                                    d={
                                        state === 'easy'
                                            ? 'M26 28 L20 34 L22 36 L28 30 Z'
                                            : state === 'stopped'
                                                ? 'M26 28 L22 34 L24 35 L28 29 Z'
                                                : 'M26 27 L16 33 L18 36 L28 30 Z'
                                    }
                                    fill={shirt}
                                />
                                <circle
                                    cx={state === 'easy' ? 21 : state === 'stopped' ? 23 : 17}
                                    cy={state === 'easy' ? 34 : state === 'stopped' ? 34 : 33}
                                    r="2.2"
                                    fill={skin}
                                />
                            </g>
                            {/* 다리 */}
                            <g className={cls("runner-leg runner-leg-front")}>
                                <path
                                    d={
                                        state === 'easy' || state === 'stopped'
                                            ? 'M33 42 L36 56 L40 56 L36 42 Z'
                                            : state === 'normal'
                                                ? 'M34 40 L44 52 L47 52 L37 40 Z'
                                                : 'M34 40 L50 50 L52 52 L37 40 Z'
                                    }
                                    fill={pants}
                                />
                                <ellipse
                                    cx={state === 'easy' || state === 'stopped' ? 38 : state === 'normal' ? 45 : 51}
                                    cy={state === 'easy' || state === 'stopped' ? 58 : state === 'normal' ? 52 : 51}
                                    rx="4"
                                    ry="1.8"
                                    fill="#2a2a2a"
                                />
                            </g>
                            <g className={cls("runner-leg runner-leg-back")}>
                                <path
                                    d={
                                        state === 'easy' || state === 'stopped'
                                            ? 'M28 42 L25 56 L29 56 L31 42 Z'
                                            : state === 'normal'
                                                ? 'M28 42 L22 54 L25 54 L31 42 Z'
                                                : 'M28 42 L18 48 L20 50 L31 42 Z'
                                    }
                                    fill={pants}
                                />
                                <ellipse
                                    cx={state === 'easy' || state === 'stopped' ? 27 : state === 'normal' ? 23 : 19}
                                    cy={state === 'easy' || state === 'stopped' ? 58 : state === 'normal' ? 54 : 49}
                                    rx="4"
                                    ry="1.8"
                                    fill="#2a2a2a"
                                />
                            </g>
                            {/* 모션 라인 (rush/panic) */}
                            {(state === 'rush' || state === 'panic') && (
                                <g className={cls("runner-motion")} stroke="#bbb" strokeWidth="1.2" fill="none">
                                    <line x1="2" y1="30" x2="14" y2="30" />
                                    <line x1="4" y1="38" x2="16" y2="38" />
                                    <line x1="6" y1="46" x2="18" y2="46" />
                                </g>
                            )}
                        </>
                    )}
                </g>

                {/* 정지 상태 ? 말풍선 */}
                {state === 'stopped' && (
                    <g>
                        <circle cx="48" cy="12" r="7" fill="#fff" stroke="#bbb" strokeWidth="1" />
                        <text x="48" y="16" fontSize="9" textAnchor="middle" fill="#444">?</text>
                    </g>
                )}
            </svg>
        </div>
    );
};

function stateColors(state: RunnerState) {
    switch (state) {
        case 'arrived':
            return { shirt: '#6FCF97', pants: '#2F80ED', face: '#fce0c2' };
        case 'easy':
            return { shirt: '#56CCF2', pants: '#2F80ED', face: '#fce0c2' };
        case 'normal':
            return { shirt: '#F2994A', pants: '#333', face: '#fbd7b5' };
        case 'rush':
            return { shirt: '#EB5757', pants: '#222', face: '#f7b7a3' };
        case 'panic':
            return { shirt: '#C0392B', pants: '#222', face: '#ff7d7d' }; // 얼굴 빨강
        case 'stopped':
            return { shirt: '#BDBDBD', pants: '#4F4F4F', face: '#fce0c2' };
    }
}

export default RunnerAvatar;
